'use client'

import styles from './Modal.module.css'
import classNames from 'classnames'
import { type MouseEvent, useEffect, useId, useRef, useState } from 'react'
import { Icon } from '../Icon/Icon'
import type { ModalFooterProps } from './ModalFooter/ModalFooter'
import { ModalFooter } from './ModalFooter/ModalFooter'
import type { ModalHeaderProps } from './ModalHeader/ModalHeader'
import { ModalHeader } from './ModalHeader/ModalHeader'

export interface ModalProps {
  children: React.ReactNode
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  // INFO: This is for any optional functionality that needs to be run when the modal is closed, i.e a success flash message
  onClose?: () => void
  variant?: 'simple' | 'page'
  isFluidWidth?: boolean
  modalContent: ModalContent
  footer?: Omit<ModalFooterProps, 'primaryButtonText' | 'secondaryButtonText'>
  headerVariant?: ModalHeaderProps['variant']
}

export interface ModalContent {
  title: string
  a11YDescription: string
  modalClose: string
  primaryButtonText: string
  secondaryButtonText: string
}

export const Modal = ({
  children,
  isOpen,
  setIsOpen,
  onClose,
  variant = 'simple',
  isFluidWidth = false,
  modalContent,
  footer,
  headerVariant = 'primary',
}: ModalProps) => {
  const modalClasses = classNames(styles.Modal, {
    [styles[`Modal--is-open`]]: isOpen,
    [styles[`Modal--is-page`]]: variant === 'page',
    [styles[`Modal--is-fluid-width`]]: isFluidWidth,
  })
  const modalContentClasses = classNames(styles.Modal__content, {
    [styles[`Modal__content--no-footer`]]: !footer,
  })

  const [returnFocusElement, setReturnFocusElement] = useState<HTMLElement | null>(null)

  const modalRef = useRef<HTMLDivElement>(null)

  const modalElement = modalRef.current

  const controller = new AbortController()
  const signal = controller.signal

  // INFO: Listening for the escape key to close the modal for keyboard a11y
  const handleEscapeKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleClose()
    }
  }

  // INFO: Checking the focus to ensure it stays within the modal for keyboard a11y
  const handleFocus = (event: FocusEvent) => {
    if (modalElement && !modalElement.contains(event.target as HTMLElement)) {
      modalElement.focus()
    }
  }

  const handleBackdropClick = (event: MouseEvent) => {
    if (modalElement?.contains(event.target as HTMLElement)) {
      return
    }
    handleClose()
  }

  const handleClose = () => {
    setIsOpen(false)
    removeEventListeners()
    returnFocusElement?.focus()
    // INFO: This is for any optional functionality that needs to be run when the modal is closed
    if (onClose) onClose()
  }

  const removeEventListeners = () => {
    controller.abort()
  }

  const headingId = useId()
  const modalId = useId()

  useEffect(() => {
    if (isOpen) {
      setReturnFocusElement(document.activeElement as HTMLElement)
      if (modalElement) {
        modalElement.focus()
      }
      document.addEventListener('keydown', handleEscapeKey, { signal })
      document.addEventListener('focus', handleFocus, { signal, capture: true })
    } else {
      handleClose()
    }

    return () => {
      removeEventListeners()
    }
  }, [isOpen])

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- This element does have key events, but they are on the document.
    <div className={modalClasses} onClick={handleBackdropClick} aria-hidden={!isOpen}>
      <div
        className={styles.Modal__box}
        tabIndex={-1}
        ref={modalRef}
        role="dialog"
        aria-labelledby={headingId}
        aria-describedby={modalId}
        aria-modal="true"
      >
        <button
          className={styles.Modal__close}
          onClick={handleClose}
          type="button"
          aria-label={modalContent.modalClose}
        >
          <Icon color={headerVariant === 'primary' ? 'white' : 'black'} name="Close" height="18px" width="18px" />
        </button>
        <ModalHeader id={headingId} heading={modalContent.title} variant={headerVariant} />
        <div id={modalId} className={styles.Modal__desc}>
          {modalContent.a11YDescription}
        </div>
        <div className={modalContentClasses}>{children}</div>
        {footer ? (
          <ModalFooter
            primaryButtonText={modalContent.primaryButtonText}
            primaryButtonFunction={footer.primaryButtonFunction}
            secondaryButtonText={modalContent.secondaryButtonText}
            secondaryButtonFunction={footer.secondaryButtonFunction}
          />
        ) : null}
      </div>
    </div>
  )
}
